import React, {ReactElement, useEffect} from "react"
import {AllowedUsersColumns, UsersColumns} from "./Users.types"
import {User} from "../../resources/user"
import {Resource} from "../../resources/common"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {useAccessToken} from "../../services/auth"
import {useToasts} from "react-toast-notifications"
import {changeUserStatus} from "../../resources/user"
import {useModal} from "react-modal-hook"
import {DisableUserModal} from "./DisableUserModal"
import {OrgName} from "../OrgName/OrgName"
import classNames from "classnames"
import {startCase} from "lodash"
import {getRoleDisplayName} from "./userRoleMapping"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"
import IdLink from "../IdLink/IdLink"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import moment from "moment"

interface UsersRowProps {
	user: User
	include?: Resource[]
	includedColumns: Array<AllowedUsersColumns>
	refresh: () => void
}

export default function UserRow({user, include, includedColumns, refresh}: UsersRowProps) {
	const navigateWithListPath = useNavigateWithListPath()
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const isDisabled = user.attributes.isDisabled
	const [statusChangeState, changeStatus] = useAsyncResultIdle(changeUserStatus)
	const [showDisableModal, hideDisableModal] = useModal(() => (
		<DisableUserModal close={hideDisableModal} refresh={refresh} user={user} />
	))

	useEffect(() => {
		statusChangeState.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title, {appearance: "error"})
		)
	}, [statusChangeState])

	const contentColumns: Partial<Record<AllowedUsersColumns, ReactElement>> = {}

	if (includedColumns.includes(UsersColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className="data-table-id" href={`/user-details/${user.id}`}>
					{user.id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(UsersColumns.org)) {
		contentColumns["Org"] =
			user.type === "orgUser" ? (
				<OrgName orgId={user.relationships.org.data.id.toString()} included={include} />
			) : (
				<DataTableCell />
			)
	}

	if (includedColumns.includes(UsersColumns.name)) {
		contentColumns["Name"] = (
			<DataTableCell>
				{user.attributes.username}
				{isDisabled ? (
					<span className="tag is-light ml-2 has-background-grey-lighter has-text-grey">Disabled</span>
				) : null}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(UsersColumns.type)) {
		contentColumns["Type"] = <DataTableCell> {startCase(user.type)} </DataTableCell>
	}

	if (includedColumns.includes(UsersColumns.role)) {
		contentColumns["Role"] = <DataTableCell> {getRoleDisplayName(user.type, user.attributes.role)} </DataTableCell>
	}

	if (includedColumns.includes(UsersColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell> {moment(user.attributes.createdAt).format("L")} </DataTableCell>
	}

	if (includedColumns.includes(UsersColumns.actions)) {
		contentColumns["Actions"] = (
			<DataTableActionButton enableActions={true}>
				<DataTableActionItem
					title={isDisabled ? "Enable" : "Disable"}
					icon={isDisabled ? "user-geometric-action-check-approve" : "user-geometric-action-cross-remove"}
					onClick={() => (isDisabled ? changeStatus(accessToken, user.id, !isDisabled) : showDisableModal())}
				/>
			</DataTableActionButton>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			className={classNames(user.attributes.isDisabled && "has-text-grey-light")}
			onClick={(e) => {
				e.preventDefault()
				e.stopPropagation()
				navigateWithListPath(`/user-details/${user.id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}
